import React, { useState, useContext } from 'react';
import { AuthContext } from '../../components/AuthWrapper';
import { Navigate, Link } from 'react-router-dom';
import { db } from '../../utils/firebase';
import { collection, doc, setDoc } from 'firebase/firestore';

// Manually import JSON data
import timelineData from '../../data/timeline.json';
import exhibitsData from '../../data/exhibits.json';
import audioData from '../../data/audio.json';
import legalClaimsData from '../../data/legal-claims.json';
import documentsData from '../../data/documents.json';

const ImportPage = () => {
  const { isAdmin } = useContext(AuthContext);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState('');
  const [results, setResults] = useState({});

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  const importCollection = async (collectionName, data) => {
    try {
      const batch = [];
      
      for (const item of data) {
        const docId = item.id ? item.id.toString() : Math.random().toString(36).substring(2, 15);
        const docRef = doc(db, collectionName, docId);
        
        // Remove the id field if it exists (since it will be the document ID)
        const docData = { ...item };
        if ('id' in docData) {
          delete docData.id;
        }
        
        batch.push(setDoc(docRef, docData));
      }
      
      // Execute all the setDoc operations
      await Promise.all(batch);
      
      return { 
        status: 'success', 
        message: `Imported ${batch.length} documents` 
      };
    } catch (error) {
      console.error(`Error importing ${collectionName}:`, error);
      return { 
        status: 'error', 
        message: `Error: ${error.message}` 
      };
    }
  };

  const handleImport = async () => {
    if (window.confirm('Are you sure you want to import all data to Firebase? This may create duplicate data if collections are not empty.')) {
      try {
        setImporting(true);
        setStatus('Starting import...');
        
        const collectionImports = [
          { name: 'timeline', data: timelineData },
          { name: 'exhibits', data: exhibitsData },
          { name: 'audio', data: audioData },
          { name: 'legal-claims', data: legalClaimsData },
          { name: 'documents', data: documentsData }
        ];
        
        const importResults = {};
        
        for (const { name, data } of collectionImports) {
          setStatus(`Importing ${name}...`);
          importResults[name] = await importCollection(name, data);
        }
        
        setResults(importResults);
        setStatus('Import completed!');
      } catch (error) {
        console.error('Import error:', error);
        setStatus(`Import failed: ${error.message}`);
      } finally {
        setImporting(false);
      }
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-navy">Direct Data Import</h1>
        <Link 
          to="/admin"
          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
        >
          Back to Dashboard
        </Link>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Import JSON to Firebase</h2>
        <p className="mb-4 text-gray-700">
          This will directly import the bundled JSON data to Firebase Firestore.
          <strong className="text-red-600"> Warning: This may create duplicate data if collections are not empty!</strong>
        </p>
        
        <button
          onClick={handleImport}
          disabled={importing}
          className={`w-full py-3 rounded font-medium ${
            importing 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {importing ? 'Importing...' : 'Start Direct Import'}
        </button>
        
        {status && (
          <div className={`mt-4 p-3 rounded ${
            status.includes('failed') 
              ? 'bg-red-100 text-red-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {status}
          </div>
        )}
      </div>
      
      {Object.keys(results).length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Import Results</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Collection
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(results).map(([collection, result]) => (
                  <tr key={collection}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {collection}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        result.status === 'success' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {result.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPage;